import { useEffect, useState } from "react";
import { useLocalization } from "@fluent/react";
import ApiHttp from "byzantine/src/ApiHttp";
import Transaction from "byzantine/src/Transaction";
import type { TransactionResponse } from "byzantine/src/Transaction";
import type Account from "byzantine/src/Account";
import { entities } from "byzantine";
import { useNotificationContext } from "cerulean";

const MAX_TRANSACTIONS = 100;

const useSettledTransactions = (accounts: Account[]) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const { send: fetchMerchants } =
    entities.merchants.useFetchMerchantsRequest();
  const [isLoading, setIsLoading] = useState(true);
  const [settledTransactions, setSettledTransactions] = useState<Transaction[]>(
    []
  );

  useEffect(() => {
    fetchMerchants();
  }, []);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        setIsLoading(true);
        const response = await ApiHttp.fetch(
          "transactions?filter=hidden&q=is:settled",
          { method: "GET" },
          { page: 1, per_page: MAX_TRANSACTIONS }
        );
        if (response?.transactions) {
          const transactions = response.transactions.map(
            (t: TransactionResponse) =>
              Transaction.deserialize(
                t,
                accounts.find((a) => a.id === t.account_id)
              )
          );
          setSettledTransactions(transactions);
        }
      } catch {
        sendNotification({
          type: "negative",
          text: l10n.getString("recent-transactions-error"),
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchTransactions();
    // Only re-fetch when account IDs change (accounts added/removed),
    // not when account properties like favorited or hidden change.
  }, [accounts.map((a) => a.id).slice().sort().join(",")]);
  return { isLoading, settledTransactions };
};

export default useSettledTransactions;
